import classNames from 'clsx';
import { get, noop } from 'lodash';
import type { TouchEvent } from 'react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router';

import { CasinoRaceCard, Leaderboard, RacePlayButton } from '@swf/casino';
import ResolverIframe from '@swf/components/Iframe/ResolverIframe';
import CloseButton, { Variant } from '@swf/components/StarsModal/components/CloseButton';
import Translation from '@swf/components/Translation/Translation';
import { Site } from '@swf/core/enums';
import { useWidget } from '@swf/core/hooks/useWidget';
import { select } from '@swf/core/store';
import type { CasinoRace } from '@swf/core/types/CasinoRaces';
import { isCompleted, isLeaderboard } from '@swf/core/utils/casino';

import {
  TXTACL_Leaderboard,
  TXTACL_Prize_Info,
  TXTCLI_Help,
  TXTWCL_Terms_and_Conditions
} from './CasinoRaceInfo.messages.json';
import styles from './CasinoRaceInfo.scss';
import PayoutList from './components/PayoutList/PayoutList';

interface LocationStateProps {
  widgetId?: string;
  path?: string;
  race?: CasinoRace;
}

enum Tabs {
  Prizes = 'one',
  Leaderboard = 'two',
  Help = 'three'
}

const tabsOrder: Tabs[] = [Tabs.Prizes, Tabs.Leaderboard, Tabs.Help];

const CasinoRaceInfo: React.FC<RouteComponentProps<object, object, LocationStateProps>> = (
  props
) => {
  const { widgetId, path = '', race } = props.location.state;
  const { widgetSelect } = useWidget(widgetId);
  const data = useSelector(widgetSelect?.get || noop);
  const raceData: CasinoRace = race || get(data, path);
  const { id, prizeDetails, status } = raceData;
  const standings = raceData.leaderboard || [];
  const showLeaderboard = isLeaderboard(status);
  const completed = isCompleted(status);
  const showTabs = showLeaderboard ? [...tabsOrder] : [tabsOrder[0], tabsOrder[2]];
  const [tabIndex, setTabIndex] = useState(0);
  const [whichTab, setWhichTab] = useState(showTabs[0]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const site = useSelector(select.site.getId);

  const tabChange = (index: number) => {
    const goTo = Math.min(Math.max(index, 0), showTabs.length - 1);
    setTabIndex(goTo);
    setWhichTab(showTabs[goTo]);
    if (detailsOpen === false) {
      toggleDetailsScreen();
    }
  };

  const toggleDetailsScreen = () => {
    setDetailsOpen(!detailsOpen);
  };

  const touchStart = (event: TouchEvent) => {
    setStartX(event.touches[0].clientX);
    setStartY(event.touches[0].clientY);
  };

  const touchEnd = (event: TouchEvent) => {
    const endX = event.changedTouches[0].clientX;
    const endY = event.changedTouches[0].clientY;
    const xDiff = startX - endX;
    const yDiff = startY - endY;
    const yBound = whichTab === Tabs.Prizes ? 220 : 100;

    if (xDiff > 100) {
      tabChange(tabIndex + 1);
    }

    if (xDiff < -100) {
      tabChange(tabIndex - 1);
    }

    if (yDiff > 40) {
      if (detailsOpen === false) {
        toggleDetailsScreen();
      }
    }

    if (startY < yBound && yDiff < -40) {
      if (detailsOpen === true) {
        toggleDetailsScreen();
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <CloseButton variant={Variant.Float} />
      </div>
      <div className={styles.panes}>
        <div className={styles.cardArea}>
          <CasinoRaceCard
            key={id}
            race={raceData}
            racesLength={1}
            widgetId={widgetId}
            path={path}
            isModal
          />
        </div>
        <div className={classNames(styles.detailsArea, detailsOpen ? styles.open : styles.closed)}>
          <span onTouchStart={touchStart} onTouchEnd={touchEnd}>
            <div className={classNames(styles.buttonsArea, showLeaderboard && styles.leaderboard)}>
              <button className={styles.tabButton} onClick={() => tabChange(0)}>
                <Translation {...TXTACL_Prize_Info} />
              </button>
              {showLeaderboard && (
                <button className={styles.tabButton} onClick={() => tabChange(1)}>
                  <Translation {...TXTACL_Leaderboard} />
                </button>
              )}
              <button className={styles.tabButton} onClick={() => tabChange(2)}>
                {site === Site.UK ? (
                  <Translation {...TXTWCL_Terms_and_Conditions} />
                ) : (
                  <Translation {...TXTCLI_Help} />
                )}
              </button>
            </div>
            <div
              className={classNames(
                styles.redBar,
                showLeaderboard && styles.leaderboard,
                styles[whichTab]
              )}
            />
            <div className={classNames(styles.detailsContent, !completed && styles.withButton)}>
              {whichTab === Tabs.Prizes && <PayoutList prizeDetails={prizeDetails} />}
              {whichTab === Tabs.Leaderboard && (
                <Leaderboard race={raceData} standings={standings} leaderboard />
              )}
              {whichTab === Tabs.Help && (
                <ResolverIframe tag="prm_casino_races_ingame" scrolling="yes" />
              )}
            </div>
          </span>
        </div>
        {!completed && (
          <div className={styles.playButton} onClick={() => toggleDetailsScreen()}>
            <RacePlayButton race={raceData} widgetId={widgetId} path={path} isModal />
          </div>
        )}
      </div>
    </div>
  );
};
export default CasinoRaceInfo;
